<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import TreeSelect from 'primevue/treeselect';
import useVuelidate from '@vuelidate/core';
import { required, requiredIf } from '@vuelidate/validators';
import { useOrganizationStore, useOrganizationTypeStore } from '@/store';
import { CustomerSuccessPartnerInterface, OrganizationInterface, OrganizationsParentsInterface } from '@/types';
import { can } from '@/composables';
import { PERMISSIONS } from '@/enum/permissions';
import { useCustomerSuccessPartnerStore } from '@/store/CustomerSuccessPartner';
const organization = ref({} as OrganizationInterface);

/**
 * All of the state references including the convertion of the
 * organization state object into a ref.
 */
const organizationStore = useOrganizationStore();
const organizationTypeStore = useOrganizationTypeStore();
const customerSuccessPartnerStore = useCustomerSuccessPartnerStore();
const parentError = ref('Cannot Add This Organization As A Parent');
const customerSuccessPartners = ref<CustomerSuccessPartnerInterface[]>([]);
const noSelectionEntry = {
  display_name: 'No selection',
  id: -1,
  first_name: '',
  last_name: '',
  person_id: 0,
  organizations: [],
  active: 0,
};
/**
 * The required validation rules.
 */
const rules = {
  name: { required },
  acronym: {},
  organization_type_id: { required },
  external_id: {},
  parent_id: {
    requiredIfCustom: requiredIf(() => !canCreateRootOrganization.value),
  },
  customer_success_partner_id: {},
};

/**
 * Initialize the vuelidate instance.
 */
const v$ = useVuelidate(rules, organization);
const canEdit = computed(() => can([PERMISSIONS.ORGANIZATION_UPDATE]));
/**
 * Throw all errors here for easier access.
 */
const errors = ref(null);

/**
 * Determines whether or not the form has been submitted.
 */
const submitted = ref(false);

/**
 * Initialize everything required to build out the component.
 */
onMounted(() => init());

/**
 * Assign the static organization identifier.
 */
const id = computed(() => organization.value.id);

const canCreateRootOrganization = computed<boolean>(() => {
  return can([PERMISSIONS.ORGANIZATION_CREATE_ROOT]);
});

/**
 * Define the request update payload.
 */
const payload = computed(() => {
  const value = organization.value;

  const { parent_id } = value;
  value.parent_id = Object.keys(parent_id)[0] ? Number(Object.keys(parent_id)[0]) : parent_id;

  return {
    name: value.name,
    acronym: value.acronym,
    organization_type_id: value.organization_type_id,
    external_id: value.external_id,
    parent_id: value.parent_id,
    customer_success_partner_id: value.customer_success_partner_id,
  };
});

/**
 * Initialize the organization and the available types.
 */
const init = async () => {
  await organizationTypeStore.getOrganizationTypes();
  await initCustomerSuccessPartners();
  setOrganizationAndParentList();
};

const initCustomerSuccessPartners = async () => {
  await customerSuccessPartnerStore.getCustomerSuccessPartners();
  customerSuccessPartners.value = [noSelectionEntry, ...customerSuccessPartnerStore.customerSuccessPartners];
};

/**
 * call parent orgs and set the org.  this is used again when
 * we update so we seperate from init since we dont need org types again
 */
const setOrganizationAndParentList = async () => {
  await organizationStore.getParentsOrganizations();
  await setOrganization();
};

/**
 * Return a cloned version of the state organization object.
 */
const clonedOrganization = () => {
  return { ...organizationStore.organization };
};

/**
 * After waiting for data to use from the API define the organization
 * ref based on a clone of the current object within state.
 */
const setOrganization = async () => (organization.value = clonedOrganization());

/**
 * Reset the vuelidate dirty status for all keys.
 */
const resetDirty = () => v$.value.$reset();

/**
 * Get the field styles based on the specific key.
 */
const fieldStyles = (key: string, styles?: string) => {
  const validation =
    (v$.value[key].$invalid && v$.value[key].$dirty) || (errors.value && errors.value[key]) ? 'p-invalid' : '';
  return `${styles} ${validation}`;
};

/**
 * Handle the cancellation of all changes made to the organization
 * by restting the values and statuses.
 */
const onCancel = () => {
  organization.value = clonedOrganization();
  errors.value = null;
  resetDirty();
};

/**
 * Handle the client side validation, followed by saving of all changes made
 * to the organization.
 */
const onSave = async () => {
  submitted.value = true;

  // Don't allow a submit event unless the fields are valid.
  if (v$.value.$invalid) return;

  // if no selection is selected for CSP, send null in the org update request
  if (!payload.value.customer_success_partner_id || payload.value.customer_success_partner_id < 0) {
    payload.value.customer_success_partner_id = null;
  }

  await organizationStore.updateOrganization(payload.value, id.value as number);
  onCancel();
  await setOrganizationAndParentList();
};

/**
 * boolean check if parent can be added to org
 * @param parentId
 */
const cannotAddOrg = (parentId: number): boolean => {
  const origParentId = organizationStore.organization.parent_id;
  const origId = organizationStore.organization.id;

  //if trying to add to itself or already a child of that org
  if (origParentId === parentId) {
    parentError.value = 'This Organization is already a child of this Parent';
    return true;
  }
  if (origId === parentId) {
    parentError.value = "You cannot select this Organization as it's own Parent";
    return true;
  }

  let cantAdd = false;
  if (organizationStore.organization.children.length) {
    cantAdd = lookForChildKey(organizationStore.organization.children as OrganizationsParentsInterface[], parentId);
  }
  return cantAdd;
};

/**
 * recursive function to seek infinite childrens key
 * @param arr
 */
const lookForChildKey = (arr: OrganizationsParentsInterface[], parentId: number) => {
  for (let i = 0; i < arr.length; i++) {
    if (arr[i].data.parent_id == parentId || arr[i].key == parentId) {
      return true;
    } else if (arr[i].children.length) {
      lookForChildKey(arr[i].children as OrganizationsParentsInterface[], parentId);
    }
  }
  return false;
};

/**
 * you cannot select the org, or the orgs children as a parent so find it here and
 * notify if not allowed and reset the org id
 */
const checkOrganizationForChildren = (e: string[]) => {
  const key = Object.keys(e)[0];

  if (cannotAddOrg(parseInt(key))) {
    organization.value.parent_id = organizationStore.organization.parent_id;
    organizationStore.doToast(parentError.value, 'Error', true);
  }
};
</script>

<template>
  <FormCard
    title="General"
    :dirty="v$.$anyDirty && canEdit"
    :cancel="onCancel"
    :save="onSave"
    :save-disabled="v$.$anyDirty && v$.$invalid"
  >
    <template #form>
      <form id="organization_profile_general" class="w-full">
        <div class="field">
          <label class="required" for="organization_profile_general_name">Name</label>
          <Skeleton v-if="organizationStore.loading" height="2.3rem" />
          <InputText
            v-else
            id="organization_profile_general_name"
            v-model="v$.name.$model"
            type="text"
            :class="fieldStyles('name')"
            placeholder="Organization Name"
            :disabled="!canEdit"
          />
        </div>
        <div class="field">
          <label class="required" for="organization_profile_general_acronym">Acronym</label>
          <Skeleton v-if="organizationStore.loading" height="2.3rem" />
          <InputText
            v-else
            id="organization_profile_general_acronym"
            v-model="v$.acronym.$model"
            type="text"
            :class="fieldStyles('acronym')"
            placeholder="Organization Acronym"
            :disabled="!canEdit"
          />
        </div>
        <div class="field">
          <label class="required" for="organization_profile_general_type_id">Type</label>
          <Skeleton v-if="organizationStore.loading" height="2.3rem" />
          <Dropdown
            v-else
            id="organization_profile_general_type_id"
            v-model="v$.organization_type_id.$model"
            :options="organizationTypeStore.types"
            option-label="title"
            option-value="id"
            :loading="organizationTypeStore.loading"
            placeholder="Select a Type"
            :class="fieldStyles('organization_type_id')"
            :disabled="!canEdit"
          />
        </div>
        <div class="field">
          <label for="organization_profile_general_external_id">SSID</label>
          <Skeleton v-if="organizationStore.loading" height="2.3rem" />
          <InputText
            v-else
            id="organization_profile_general_external_id"
            v-model="v$.external_id.$model"
            type="text"
            :class="fieldStyles('external_id')"
            placeholder="SSID"
            :disabled="!canEdit"
          />
        </div>
        <div class="formgrid grid field col mb-0">
          <label class="required" for="type">Parent Organization</label>
          <Skeleton v-if="organizationStore.loading" height="2.3rem" />
          <TreeSelect
            v-else
            id="parent_organization"
            v-model="v$.parent_id.$model"
            :options="organizationStore.organizationParents"
            option-label="label"
            option-value="key"
            :loading="organizationStore.loading"
            placeholder="Select Parent Organization"
            :disabled="!canEdit"
            style="font-weight: bold"
            class="w-full"
            @change="checkOrganizationForChildren"
          />
        </div>
        <div v-if="customerSuccessPartnerStore.canGetCustomerSuccessPartners" class="field">
          <label for="organization_profile_general_customer_success_partner_id">CSP</label>
          <Skeleton v-if="organizationStore.loading" height="2.3rem" />
          <Dropdown
            v-else
            id="organization_profile_general_customer_success_partner_id"
            v-model="v$.customer_success_partner_id.$model"
            :options="customerSuccessPartners"
            option-label="display_name"
            option-value="id"
            :loading="customerSuccessPartnerStore.loading"
            placeholder="Select a CSP"
            :disabled="!canEdit"
          />
        </div>
      </form>
    </template>
  </FormCard>
</template>
